import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  UseQueryOptions,
  UseMutationOptions,
} from 'react-query'
import { UsersApi } from '@trybeapp/sdk.auth'
import { useCurrentSiteId } from 'contexts/SiteConfig'
import { usePageQueryParam } from 'hooks/UsePageQueryParam'
import {
  ActionUsersPurgeSessionsRequest,
  ListAllUsersRequest,
  ListUsers,
  ResponseError,
  ShowUser,
  SiteResponse1,
  User,
  UsersApi as TsUsersApi,
} from '@trybeapp/sdk'
import { apiConfiguration } from 'utilities/TrybeApiClient'
import { LegacySdkResponseError } from 'api/api.types'
import { useUser as useAuthenticatedUser } from 'hooks/UseUser'

const legacyApi = new UsersApi()
const api = new TsUsersApi(apiConfiguration)

export const useUsers = (
  params: ListAllUsersRequest = {},
  options?: UseQueryOptions<ListUsers, ResponseError>
) => {
  const siteId = useCurrentSiteId()
  params.perPage = params.perPage ?? 100

  return useQuery<ListUsers, ResponseError>(
    ['users', siteId, params],
    () =>
      api.listAllUsers({
        ...params,
        perPage: params.perPage ?? 100,
        siteId: siteId,
      }),
    options
  )
}

export const useInfiniteUsers = (params: ListAllUsersRequest = {}) => {
  const siteId = useCurrentSiteId()

  return useInfiniteQuery(
    ['infiniteUsers'],
    async ({ pageParam = 1 }) => {
      const response = await api.listAllUsers({
        ...params,
        page: pageParam,
        siteId,
      })
      return {
        data: response.data,
      }
    },
    {
      getNextPageParam: (lastPage: any) => lastPage.nextPage,
    }
  )
}

/**
 * @deprecated
 * @see useUser
 */
export const useGetUser = (userId: string, options = {}) => {
  return useQuery(['users', userId], () => legacyApi.userShow(userId), options)
}

export const usePagedUsers = (params = {}) => {
  const [page] = usePageQueryParam()

  return useUsers({ page, perPage: 0, ...params })
}

export const useUser = (
  userId?: User['id'] | null,
  options: UseQueryOptions<ShowUser, ResponseError> = {}
) => {
  const user = useAuthenticatedUser()

  userId ??= user.sub

  return useQuery<ShowUser, ResponseError>(
    ['users', userId],
    () => api.getUser({ userId }),
    options
  )
}

export const useAddUser = () => {
  const queryClient = useQueryClient()

  return useMutation<ShowUser, LegacySdkResponseError, Record<string, any>>(
    (user) => legacyApi.userStore(user),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users')
      },
    }
  )
}

export const useUpdateUser = () => {
  const queryClient = useQueryClient()

  return useMutation<ShowUser, LegacySdkResponseError, [string, Record<string, any>]>(
    ([userId, user]) => legacyApi.userUpdate(userId, user),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users')
      },
    }
  )
}

export const useDeleteUser = () => {
  const queryClient = useQueryClient()

  return useMutation<void, LegacySdkResponseError, string>(
    (userId) => legacyApi.userDestroy(userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users')
      },
    }
  )
}

export const useResetUserPassword = () => {
  return useMutation<any, LegacySdkResponseError, string>((userId) =>
    legacyApi.userPasswordReset(userId)
  )
}

export const useDisableUser2fa = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<any, LegacySdkResponseError, string>(
    (userId) => legacyApi.userTwoFactorAuthenticationDisable(userId, siteId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users')
      },
    }
  )
}

export const useGetUserSites = (
  userId: string,
  options: UseQueryOptions<SiteResponse1, ResponseError> = {}
) => {
  return useQuery<SiteResponse1, ResponseError>(
    ['userSites', userId],
    () => api.getUserSites({ userId }),
    options
  )
}

export function useLogoutUsers(
  options: UseMutationOptions<void, ResponseError, ActionUsersPurgeSessionsRequest> = {}
) {
  return useMutation<void, ResponseError, ActionUsersPurgeSessionsRequest | void>(
    (params) => api.actionUsersPurgeSessions(params ?? {}),
    options
  )
}
